import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/**
 * Parses a Python dict/list string into a JS array.
 * Handles single-quoted keys/values and trailing text.
 */
export function parsePythonToJSON<T>(raw: string): T[] {
	const trimmed = raw.trim();

	// Try standard JSON first
	try {
		const parsed = JSON.parse(trimmed);
		return Array.isArray(parsed) ? parsed : [parsed];
	} catch {
		// Fall through to Python dict handling
	}

	// Extract the outermost dict/array
	const match = trimmed.match(/[[{][\s\S]*[\]}]/);
	if (!match) return [];

	let cleaned = match[0];
	const ph = "##APO##";
	// Protect apostrophes inside double-quoted strings
	cleaned = cleaned.replace(/"([^"]*?)"/g, (m) => m.replace(/'/g, ph));
	// Convert Python single quotes to JSON double quotes
	cleaned = cleaned.replace(/'/g, '"');
	// Restore apostrophes
	cleaned = cleaned.replaceAll(ph, "'");

	try {
		const parsed = JSON.parse(cleaned);
		return Array.isArray(parsed) ? parsed : [parsed];
	} catch {
		return [];
	}
}

export function formatNumberWithSuffix(
	value: string | number | null | undefined,
): string {
	if (value === null || value === undefined) return "N/A";
	if (value === "N/A") return value;

	const num = parseFloat(value.toString());
	if (Number.isNaN(num)) return "N/A";

	const absNum = Math.abs(num);

	const formatValue = (val: number) => {
		const formatted = val.toFixed(2);
		return formatted.endsWith(".00") ? formatted.slice(0, -3) : formatted;
	};

	if (absNum >= 1e12) {
		return `$${formatValue(num / 1e12)}T`;
	} else if (absNum >= 1e9) {
		return `$${formatValue(num / 1e9)}B`;
	} else if (absNum >= 1e6) {
		return `$${formatValue(num / 1e6)}M`;
	} else if (absNum >= 1e3) {
		return `$${formatValue(num / 1e3)}K`;
	} else {
		return `$${formatValue(num)}`;
	}
}
